<!-- DsMapCard.vue -->
<template>
  <div >
    <div
      class="ds-card-wrapper"
      :class="{ 'is-centered': centered }"
      ref="wrapper"
      :style="{
        '--card-w': baseWidth + 'px',
        '--scale': scale,
      }"
    >
      <!-- Stage reserves the scaled height so the layout doesn't collapse -->
      <div class="stage" :style="{ height: scaledHeight + 'px' }">
        <div class="card-canvas" ref="canvas">
          <HelzbergCard
            v-if="isHelzberg"
            ref="pdfDownload"
            :certificate="certificate"
            :certImage="certImage"
          />
          <DISCard
            v-else
            ref="pdfDownload"
            :certificate="certificate"
            :certImage="certImage"
          />
        </div>
      </div>

      <!-- <div class="print-button">
        <button class="search" @click="downloadPDF">Download PDF</button>
      </div> -->
    </div>
  </div>
</template>

<script>
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { DateTime } from "luxon";
import DISCard from "./DISCard.vue";
import HelzbergCard from "./HelzbergCard.vue";

export default {
  name: "DsMapCard",
  components: { DISCard, HelzbergCard },
  props: {
    // Base design width of your card component (everything scales from here)
    width: { type: String, default: "640px" },
    centered: { type: Boolean, default: true },
    bandColor: { type: String, default: "#0c1733" },
    bandHeight: { type: String, default: "35%" },
    logoSrc: { type: String, default: "" },
    mapSrc: { type: String, default: "" },
    certificate: { type: Object, default: () => ({}) },
    isHelzberg: { type: Boolean, default: true },
    certImage: { type: String, default: "" },
  },
  data() {
    return {
      baseWidth: 640, // px, matches your original
      scale: 1, // computed vs container width
      ro: null,
      scaledHeight: 0, // stage height after scaling
    };
  },
  computed: {
    CertDate() {
      return this.certificate?.created
        ? DateTime.fromMillis(this.certificate.created).toFormat("MM/dd/yyyy")
        : "";
    },
  },
  mounted() {
    // parse width prop if provided
    const num = parseInt(this.width, 10);
    if (!isNaN(num)) this.baseWidth = num;

    this.$nextTick(() => {
      this.measure();
      // Resize observer keeps things perfect on rotation / resizes
      this.ro = new ResizeObserver(() => this.measure());
      this.ro.observe(this.$refs.wrapper);
    });
  },
  beforeDestroy() {
    if (this.ro) this.ro.disconnect();
  },
  unmounted() {
    if (this.ro) this.ro.disconnect();
  },
  methods: {
    measure() {
      const wrapper = this.$refs.wrapper;
      const canvas = this.$refs.canvas;
      if (!wrapper || !canvas) return;

      // Scale to fit wrapper's inner width (with a small gutter)
      const maxW = wrapper.clientWidth;
      const s = Math.min(1, maxW / this.baseWidth);
      this.scale = s;

      // Measure the unscaled canvas height and multiply by scale to reserve space
      const rect = canvas.getBoundingClientRect();
      // rect is after CSS transform; get natural height from scrollHeight to be safe
      const naturalH = canvas.scrollHeight || rect.height / s || 0;
      this.scaledHeight = Math.ceil(naturalH * s);
    },

    async downloadPDF() {
      const el = this.$refs.pdfDownload?.$el;
      if (!el) return;

      await this.$nextTick();
      await new Promise((r) => setTimeout(r, 100));
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch (err) {
          console.log(err);
        }
      }

      try {
        // Clone & replace canvases with imgs (QR etc.)
        const clone = el.cloneNode(true);
        const liveCanvases = el.querySelectorAll("canvas");
        const cloneCanvases = clone.querySelectorAll("canvas");
        liveCanvases.forEach((cv, i) => {
          try {
            const dataURL = cv.toDataURL("image/png");
            const img = new Image();
            img.src = dataURL;
            img.style.width =
              cv.style.width || (cv.width ? `${cv.width}px` : "");
            img.style.height =
              cv.style.height || (cv.height ? `${cv.height}px` : "");
            const target = cloneCanvases[i];
            if (target && target.parentNode)
              target.parentNode.replaceChild(img, target);
          } catch (err) {
            console.log(err);
          }
        });

        const stage = document.createElement("div");
        stage.style.cssText =
          "position:fixed;left:-99999px;top:-99999px;background:#fff;";
        stage.appendChild(clone);
        document.body.appendChild(stage);

        const canvas = await html2canvas(clone, {
          backgroundColor: "#ffffff",
          scale: 3,
          useCORS: true,
          logging: false,
          removeContainer: true,
        });
        stage.remove();

        const pageW = 1056,
          pageH = 816;
        const pdf = new jsPDF({
          unit: "px",
          format: [pageW, pageH],
          orientation: "landscape",
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.98);
        const imgW = canvas.width,
          imgH = canvas.height;
        const maxW = pageW / 2,
          maxH = pageH / 2;
        const k = Math.min(maxW / imgW, maxH / imgH, 1);
        const drawW = imgW * k,
          drawH = imgH * k;
        const x = (pageW - drawW) / 2,
          y = (pageH - drawH) / 2;

        pdf.addImage(imgData, "JPEG", x, y, drawW, drawH);
        pdf.save(`Certificate #${this.certificate?.CertNum || ""}.pdf`);
      } catch (error) {
        console.log("error printing pdf", { error });
      }
    },

    // --- your helpers (unchanged) ---
    handleWeight(item) {
      /* ... keep your existing code ... */
    },
    handleShapes(item) {
      /* ... keep your existing code ... */
    },
    handleClarity(item) {
      /* ... keep your existing code ... */
    },
    handleColor(item) {
      /* ... keep your existing code ... */
    },
    handleCut(item) {
      /* ... keep your existing code ... */
    },
  },
};
</script>

<style scoped>
/* Fullscreen safe-area container */
/* .screen {
  min-height: 100dvh;
  padding: calc(env(safe-area-inset-top, 0px) + 16px) 16px
    calc(env(safe-area-inset-bottom, 0px) + 24px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
} */

/* Wrapper centers content and gives a max width on large screens */
.ds-card-wrapper {
  width: 100%;
  max-width: min(820px, 100%); /* wider on desktop, still centered */
  font-weight: 600;
  display: grid;
  justify-items: center;
  row-gap: 16px;
  justify-content: center;
}

.ds-card-wrapper.is-centered {
  justify-items: center;
}

/* Stage reserves vertical space equal to the scaled card height */
.stage {
  width: 100%;
}

/* The “design canvas” is always baseWidth wide; we scale it down on small screens */
.card-canvas {
  width: var(--card-w);
  transform-origin: top center;
  transform: scale(var(--scale));
  /* Prevent Safari blurriness on scaled elements */
  -webkit-font-smoothing: antialiased;
  backface-visibility: hidden;
}

/* Button area */
.print-button {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 8px;
}

.search {
  width: min(480px, 100%);
  padding: 14px 18px;
  border: 0;
  border-radius: 999px;
  font-weight: 800;
  font-size: clamp(14px, 4vw, 18px);
  background: #2f6df6;
  color: #fff;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.25);
  cursor: pointer;
}

.search:active {
  transform: translateY(1px);
}

/* Small devices: keep things compact */
@media (max-width: 399px) {
  .search {
    padding: 12px 16px;
  }
}

/* Tablets & up: give more breathing room */
@media (min-width: 768px) {
  .ds-card-wrapper {
    row-gap: 20px;
  }
}
</style>
